/**
 * useCreateDatabaseConnection Hook
 *
 * Custom React Query mutation hook for creating database connections.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { databaseConnectionService } from '../../../api/database-connection.service';
import type { DatabaseConnectionCreateDTO } from '../../../types/database-connection.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateDatabaseConnectionOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating database connection
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateDatabaseConnection = (tenantId: string, options?: UseCreateDatabaseConnectionOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DatabaseConnectionCreateDTO) => databaseConnectionService.createDatabaseConnection(tenantId, data),
    onSuccess: () => {
      // Invalidate all database connection queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['database-connections', tenantId],
        exact: false
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
